import { useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import type { Step4Data } from '../../../types/onboarding';

interface Step4Props {
  data: Step4Data | null;
  onChange: (data: Step4Data) => void;
  onNext: () => void;
  onBack: () => void;
  businessName: string;
  saving: boolean;
}

export function Step4BusinessDetails({
  data,
  onChange,
  onNext,
  onBack,
  businessName,
  saving,
}: Step4Props) {
  const [description, setDescription] = useState(data?.business_description || '');
  const [tagline, setTagline] = useState(data?.tagline || '');
  const [services, setServices] = useState(data?.services || '');
  const [yearsInBusiness, setYearsInBusiness] = useState<number | ''>(
    data?.years_in_business || ''
  );
  const [whatMakesUnique, setWhatMakesUnique] = useState(data?.what_makes_unique || '');

  // Save current state to parent on blur
  const saveData = () => {
    onChange({
      business_description: description,
      tagline: tagline || undefined,
      services,
      years_in_business: yearsInBusiness ? Number(yearsInBusiness) : undefined,
      what_makes_unique: whatMakesUnique || undefined,
    });
  };

  const descriptionLength = description.length;
  const canProceed = description.length >= 50 && services.trim().length > 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Tell us about {businessName}
        </h1>
        <p className="text-gray-600">
          This information helps us create compelling content for your website.
        </p>
      </div>

      {/* Business description */}
      <div className="space-y-2">
        <label className="block text-lg font-semibold text-gray-900">
          Business Description *
        </label>
        <p className="text-sm text-gray-500">
          Describe what your business does and what makes it special.
        </p>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={saveData}
          rows={4}
          placeholder="Example: We're a family-owned Italian restaurant serving authentic recipes passed down through three generations. Our handmade pasta and wood-fired pizzas have been delighting customers in downtown Chicago since 1985."
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
        />
        <div className="flex justify-between text-sm">
          <span
            className={descriptionLength < 50 ? 'text-amber-600' : 'text-gray-500'}
          >
            {descriptionLength < 50
              ? `At least ${50 - descriptionLength} more characters needed`
              : 'Great length!'}
          </span>
          <span className="text-gray-400">{descriptionLength}/500</span>
        </div>
      </div>

      {/* Tagline */}
      <div className="space-y-2">
        <label className="block text-lg font-semibold text-gray-900">
          Tagline
          <span className="text-gray-400 font-normal text-sm ml-2">Optional</span>
        </label>
        <p className="text-sm text-gray-500">
          A catchy phrase that captures your brand. Leave blank and our AI will
          create one.
        </p>
        <input
          type="text"
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          onBlur={saveData}
          placeholder="Example: Taste the tradition"
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Services */}
      <div className="space-y-2">
        <label className="block text-lg font-semibold text-gray-900">
          Services / Offerings *
        </label>
        <p className="text-sm text-gray-500">
          List your main services or offerings, one per line.
        </p>
        <textarea
          value={services}
          onChange={(e) => setServices(e.target.value)}
          onBlur={saveData}
          rows={4}
          placeholder="Example:
Dine-in service
Takeout and delivery
Catering for events
Private dining room"
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none font-mono text-sm"
        />
      </div>

      {/* Years in business */}
      <div className="space-y-2">
        <label className="block text-lg font-semibold text-gray-900">
          Years in Business
          <span className="text-gray-400 font-normal text-sm ml-2">Optional</span>
        </label>
        <p className="text-sm text-gray-500">
          How long have you been serving customers?
        </p>
        <input
          type="number"
          value={yearsInBusiness}
          onChange={(e) =>
            setYearsInBusiness(e.target.value ? parseInt(e.target.value) : '')
          }
          onBlur={saveData}
          min="0"
          max="200"
          placeholder="e.g., 15"
          className="w-32 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* What makes you unique */}
      <div className="space-y-2">
        <label className="block text-lg font-semibold text-gray-900">
          What Makes You Different?
          <span className="text-gray-400 font-normal text-sm ml-2">Optional</span>
        </label>
        <p className="text-sm text-gray-500">
          What sets your business apart from the competition? This helps us highlight your unique value.
        </p>
        <textarea
          value={whatMakesUnique}
          onChange={(e) => setWhatMakesUnique(e.target.value)}
          onBlur={saveData}
          rows={3}
          maxLength={300}
          placeholder="Example: We're the only shop in town that uses locally-sourced, organic ingredients. Every item is made fresh daily, and we offer free delivery for orders over $50."
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
        />
        <div className="flex justify-end text-sm">
          <span className="text-gray-400">{whatMakesUnique.length}/300</span>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed || saving}
          className={`
            flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
            ${
              canProceed
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
